import React, { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Text } from "@hangar/web-components/core/Text";
import { Spinner } from "@hangar/web-components/core/Spinner";
import { useUserRoles } from "../../hooks/auth";
import { useTranslation } from "../../hooks/i18n";
import { UserRole, UserRoleInfo } from "../../services/auth";
import { ProtectedRoute } from "./ProtectedRoute";
import {
  LoadingContainer,
  ErrorContainer,
  AccessDeniedContainer,
  AccessDeniedAlert,
  AccessDeniedIllustration,
  AccessDeniedTitle,
  AccessDeniedDescription,
  AccessDeniedDescriptionLine,
  AccessDeniedEmailLink,
  ErrorAlert,
} from "./RoleProtectedRoute.styles";

const HANGAR_STATICS_BASE = import.meta.env.PROD
  ? "https://hangar-statics.appslatam.com"
  : "https://hangar-statics.dev.appslatam.com";

const ILLUSTRATION_ACCESS_DENIED_URL = `${HANGAR_STATICS_BASE}/images/illustrations/device/DeviceStrongPassword.svg`;

const ACCESS_REQUEST_EMAIL = "grp_insightpro_notifications@latam.com";

interface RoleProtectedRouteProps {
  children: ReactNode;
  requiredRoles: (UserRole | string)[];
  requireAll?: boolean;
  fallback?: ReactNode;
  redirectTo?: string;
  skipAuthCheck?: boolean;
  loadingFallback?: ReactNode;
}

export const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  children,
  requiredRoles,
  requireAll = false,
  fallback,
  redirectTo,
  skipAuthCheck = false,
  loadingFallback,
}) => {
  const { hasAnyRole, hasAllRoles, getUserRoles, loading, error, userRoleInfo } = useUserRoles();
  const navigate = useNavigate();

  const roleCheckContent = (
    <RoleCheck
      requiredRoles={requiredRoles}
      requireAll={requireAll}
      fallback={fallback}
      redirectTo={redirectTo}
      hasAnyRole={hasAnyRole}
      hasAllRoles={hasAllRoles}
      getUserRoles={getUserRoles}
      loading={loading}
      error={error}
      userRoleInfo={userRoleInfo}
      navigate={navigate}
      loadingFallback={loadingFallback}
    >
      {children}
    </RoleCheck>
  );

  return skipAuthCheck ? roleCheckContent : <ProtectedRoute>{roleCheckContent}</ProtectedRoute>;
};

interface RoleCheckProps {
  children: ReactNode;
  requiredRoles: (UserRole | string)[];
  requireAll: boolean;
  fallback?: ReactNode;
  redirectTo?: string;
  hasAnyRole: (roles: (UserRole | string)[]) => boolean;
  hasAllRoles: (roles: (UserRole | string)[]) => boolean;
  getUserRoles: () => string[];
  loading: boolean;
  error: string | null;
  userRoleInfo: UserRoleInfo | null;
  navigate: (path: string) => void;
  loadingFallback?: ReactNode;
}

const RoleCheck: React.FC<RoleCheckProps> = ({
  children,
  requiredRoles,
  requireAll,
  fallback,
  redirectTo,
  hasAnyRole,
  hasAllRoles,
  getUserRoles,
  loading,
  error,
  userRoleInfo,
  navigate,
  loadingFallback,
}) => {
  const { t } = useTranslation();
  const rolesNotYetResolved = !error && userRoleInfo === null;
  if (loading || rolesNotYetResolved) {
    if (loadingFallback) {
      return <>{loadingFallback}</>;
    }
    return (
      <LoadingContainer>
        <Spinner size="normal" />
        <Text variant="body-lg">Verificando permisos...</Text>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <ErrorAlert>
          <Text variant="body-lg">Error verificando permisos</Text>
          {error}
        </ErrorAlert>
      </ErrorContainer>
    );
  }

  const hasPermission = requireAll ? hasAllRoles(requiredRoles) : hasAnyRole(requiredRoles);

  if (!hasPermission) {
    if (redirectTo) {
      navigate(redirectTo);
      return null;
    }

    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <AccessDeniedContainer>
        <AccessDeniedIllustration
          src={ILLUSTRATION_ACCESS_DENIED_URL}
          alt={t("auth.accessDenied.title")}
        />
        <AccessDeniedAlert>
          <AccessDeniedTitle>
            <Text variant="heading-lg" align="center" color="tertiary" bold={true}>
              {t("auth.accessDenied.title")}
            </Text>
          </AccessDeniedTitle>
          <AccessDeniedDescription>
            <Text variant="body-lg" align="center">
              {t("auth.accessDenied.description")}
            </Text>
            <AccessDeniedDescriptionLine>
              <Text variant="body-lg" align="center">
                {t("auth.accessDenied.requestAccessEmailIntro")}{" "}
                <AccessDeniedEmailLink href={`mailto:${ACCESS_REQUEST_EMAIL}`}>
                  {ACCESS_REQUEST_EMAIL}
                </AccessDeniedEmailLink>
              </Text>
            </AccessDeniedDescriptionLine>
          </AccessDeniedDescription>
        </AccessDeniedAlert>
      </AccessDeniedContainer>
    );
  }

  return <>{children}</>;
};

export const withRoleProtection = <P extends object>(
  Component: React.ComponentType<P>,
  requiredRoles: (UserRole | string)[],
  requireAll: boolean = false
) => {
  return (props: P) => (
    <RoleProtectedRoute requiredRoles={requiredRoles} requireAll={requireAll}>
      <Component {...props} />
    </RoleProtectedRoute>
  );
};

export default RoleProtectedRoute;
